import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocationStore } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, Building2, CheckCircle2, Link2, Loader2, Search } from "lucide-react";
import type { Location } from "@shared/schema";

interface GbpLocation {
  resourceName: string;
  accountName: string;
  title: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  rating: number;
  totalReviews: number;
  verificationState: string;
  gbpStatus: string;
  primaryCategory: string;
  phone: string;
  website: string;
  placeId: string;
  lat: number;
  lng: number;
}

interface GbpLocationsResponse {
  connected: boolean;
  locations: GbpLocation[];
  error?: string;
  needsReconnect?: boolean;
  needsGbpScope?: boolean;
  debug?: { reason?: string; scope?: string; email?: string; error?: string };
}

export default function LinkGbpPage() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { selectedLocationId, setSelectedLocationId } = useLocationStore();
  const [search, setSearch] = useState("");
  const [linkingResource, setLinkingResource] = useState<string | null>(null);

  const returnTo = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const r = params.get("returnTo");
    return r && /^\/[A-Za-z0-9_\-/]*$/.test(r) ? r : "/profile-health";
  }, []);

  const { data: locations = [], isLoading: locationsLoading } = useQuery<Location[]>({
    queryKey: ["/api/locations"],
    staleTime: 0,
    refetchOnMount: "always",
  });

  const workspaceLocation = locations.find((l) => l.id === selectedLocationId) || locations[0];

  const { data: gbpData, isLoading: gbpLoading, refetch } = useQuery<GbpLocationsResponse>({
    queryKey: ["/api/google/locations"],
  });

  const linkMutation = useMutation({
    mutationFn: async ({ workspaceId, gbp }: { workspaceId: string | null; gbp: GbpLocation }) => {
      const url = workspaceId
        ? `/api/locations/${workspaceId}/link-gbp`
        : `/api/locations/from-gbp`;
      const res = await apiRequest("POST", url, gbp);
      return res.json();
    },
    onSuccess: (data: Location) => {
      if (data?.id) setSelectedLocationId(data.id);
      queryClient.invalidateQueries({ queryKey: ["/api/locations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/gbp/scope-check"] });
      queryClient.invalidateQueries({ queryKey: ["/api/gbp/profile-completeness"] });
      toast({
        title: "Linked!",
        description: "Your Google Business Profile is now connected. Pulling fresh data…",
      });
      navigate(returnTo);
    },
    onError: (err: Error) => {
      setLinkingResource(null);
      toast({
        title: "Could not link profile",
        description: err.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleLink = (gbp: GbpLocation) => {
    setLinkingResource(gbp.resourceName);
    linkMutation.mutate({ workspaceId: workspaceLocation?.id || null, gbp });
  };

  useEffect(() => {
    if (!workspaceLocation && locations.length === 0) return;
  }, [workspaceLocation, locations]);

  const gbpLocations = gbpData?.locations || [];
  const filtered = gbpLocations.filter((l) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      l.title.toLowerCase().includes(q) ||
      l.address.toLowerCase().includes(q) ||
      l.city.toLowerCase().includes(q)
    );
  });

  return (
    <div className="p-6 pb-24 space-y-6 overflow-y-auto h-full max-w-4xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold tracking-tight" data-testid="text-link-gbp-title">
          Link Your Google Business Profile
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Pick the Google Business Profile to attach to this workspace location. We'll copy in your business name,
          address, phone, website and category so the rest of the app can use them.
        </p>
      </div>

      {workspaceLocation && (
        <Card className="border-emerald-500/30 bg-emerald-500/5" data-testid="card-target-location">
          <CardContent className="p-4 flex items-center gap-3">
            <Building2 className="w-5 h-5 text-emerald-400 shrink-0" />
            <div className="flex-1">
              <p className="text-xs text-muted-foreground">Linking to</p>
              <p className="text-sm font-semibold" data-testid="text-target-location-name">
                {workspaceLocation.name}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate(returnTo)} data-testid="button-skip-link">
              Skip for now
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search by name, address, or city…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid="input-search-gbp"
        />
      </div>

      {gbpLoading ? (
        <div className="space-y-3" data-testid="state-loading-gbps">
          <div className="flex items-center justify-center gap-2 py-3 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Loading your Google Business Profiles… this can take a few seconds.</span>
          </div>
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : gbpData?.needsReconnect || gbpData?.needsGbpScope ? (
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-10 h-10 text-amber-400 mx-auto mb-2" />
            <p className="text-sm font-medium" data-testid="text-reconnect-headline">
              {gbpData?.debug?.reason === "no_token_row" && "No Google account is connected"}
              {gbpData?.debug?.reason === "scope_missing_business_manage" && "Google Business Profile permission was not granted"}
              {gbpData?.debug?.reason === "no_refresh_token" && "Google connection is incomplete"}
              {gbpData?.debug?.reason === "refresh_failed" && "Google connection has expired"}
              {!gbpData?.debug?.reason && "Google connection needs to be refreshed"}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {gbpData?.debug?.reason === "no_token_row" && "Click below to authenticate with Google and pick which account owns your Google Business Profile."}
              {gbpData?.debug?.reason === "scope_missing_business_manage" && (
                <>You connected as <span className="font-mono">{gbpData?.debug?.email || "your Google account"}</span>, but the "Manage your Google Business Profile" permission was not granted. On the next consent screen make sure that box stays checked.</>
              )}
              {gbpData?.debug?.reason === "no_refresh_token" && "Google did not return a refresh token. Reconnecting from a fresh consent screen will fix this."}
              {gbpData?.debug?.reason === "refresh_failed" && (
                <>Google rejected the refresh token. This usually means access was revoked at <a href="https://myaccount.google.com/permissions" target="_blank" rel="noreferrer" className="underline">myaccount.google.com/permissions</a>. Reconnect to get a fresh token.</>
              )}
              {!gbpData?.debug?.reason && "Click below to reconnect."}
            </p>
            {gbpData?.debug && (
              <p className="text-[10px] text-muted-foreground/60 mt-3 font-mono" data-testid="text-debug-reason">
                debug: {JSON.stringify(gbpData.debug)}
              </p>
            )}
            <Button
              size="sm"
              className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={async () => {
                const res = await fetch(`/api/google/auth-url?returnTo=${encodeURIComponent(returnTo)}`);
                const data = await res.json();
                if (data.authUrl) window.location.href = data.authUrl;
              }}
              data-testid="button-reconnect-google"
            >
              <Link2 className="w-3.5 h-3.5 mr-1.5" /> Reconnect Google
            </Button>
          </CardContent>
        </Card>
      ) : gbpLocations.length === 0 ? (
        <Card className="border-border/50">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-2" />
            <p className="text-sm font-medium" data-testid="text-no-gbps">
              No Google Business Profiles found on this Google account
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {gbpData?.error || "Make sure the account you connected is a manager or owner of a Google Business Profile."}
            </p>
            <Button variant="outline" size="sm" className="mt-4" onClick={() => refetch()} data-testid="button-retry-gbps">
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6" data-testid="text-no-search-matches">
              No profiles match "{search}".
            </p>
          )}
          {filtered.map((gbp) => {
            const isLinking = linkingResource === gbp.resourceName;
            const alreadyLinked = workspaceLocation?.gbpLocationId === gbp.resourceName;
            return (
              <Card key={gbp.resourceName} className="border-border/50 hover-elevate" data-testid={`card-gbp-${gbp.placeId || gbp.resourceName}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-base truncate" data-testid={`text-gbp-title-${gbp.placeId || gbp.resourceName}`}>
                        {gbp.title}
                      </CardTitle>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {gbp.address}{gbp.city ? `, ${gbp.city}` : ""}{gbp.state ? `, ${gbp.state}` : ""} {gbp.zip}
                      </p>
                    </div>
                    {gbp.verificationState === "VERIFIED" && (
                      <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 shrink-0">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Verified
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-xs text-muted-foreground space-x-3">
                      {gbp.primaryCategory && <span data-testid={`text-gbp-cat-${gbp.placeId || gbp.resourceName}`}>{gbp.primaryCategory}</span>}
                      {gbp.totalReviews > 0 && (
                        <span>★ {gbp.rating.toFixed(1)} ({gbp.totalReviews} reviews)</span>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleLink(gbp)}
                      disabled={isLinking || alreadyLinked || locationsLoading}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`button-link-${gbp.placeId || gbp.resourceName}`}
                    >
                      {isLinking ? (
                        <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Linking…</>
                      ) : alreadyLinked ? (
                        <><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Linked</>
                      ) : locationsLoading ? (
                        <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Loading…</>
                      ) : (
                        <><Link2 className="w-3.5 h-3.5 mr-1.5" /> Link this profile</>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
